export class ProductRecommendations extends HTMLElement {
	static tagName = "product-recommendations";
	static {
		customElements.define(ProductRecommendations.tagName, ProductRecommendations);
	}
	/**
	 * The observer for the product recommendations
	 * @type {IntersectionObserver}
	 */
	#intersectionObserver = new IntersectionObserver(
		(entries, observer) => {
			if (!entries[0]?.isIntersecting) return;

			observer.disconnect();
			this.#loadRecommendations();
		},
		{ rootMargin: "0px 0px 400px 0px" },
	);

	/**
	 * Observing changes to the elements attributes
	 * @type {MutationObserver}
	 */
	#mutationObserver = new MutationObserver((mutations) => {
		for (const mutation of mutations) {
			// Only attribute changes are interesting
			if (mutation.target !== this || mutation.type !== "attributes") continue;

			// Ignore error attribute changes
			if (mutation.attributeName === "data-error") continue;

			// Ignore addition of hidden class because it means there's an error with the display
			if (mutation.attributeName === "class" && this.classList.contains("hidden")) continue;

			// Ignore when the data-recommendations-performed attribute has been set to 'true'
			if (
				mutation.attributeName === "data-recommendations-performed" &&
				this.dataset.recommendationsPerformed === "true"
			)
				continue;

			// All other attribute changes trigger a reload
			this.#loadRecommendations();
			break;
		}
	});

	connectedCallback() {
		this.#intersectionObserver.observe(this);
		this.#mutationObserver.observe(this, { attributes: true });
	}

	disconnectedCallback() {
		this.#intersectionObserver?.disconnect();
		this.#mutationObserver?.disconnect();
	}

	#loadRecommendations() {
		// If the recommendations have already been loaded, accounts for the case where the Theme Editor
		// is loaded the section from the editor's visual preview context.
		if (this.dataset.recommendationsPerformed === "true") {
			return;
		}

		fetch(this.dataset.url)
			.then((response) => response.text())
			.then((text) => {
				const html = document.createElement("div");
				html.innerHTML = text;
				const recommendations = html.querySelector("product-recommendations");

				if (recommendations?.innerHTML && recommendations.innerHTML.trim().length > 0) {
					this.dataset.recommendationsPerformed = "true";
					this.innerHTML = recommendations.innerHTML;
				}
			})
			.catch((error) => {
				console.error(error);
				window.Bugsnag?.notify(error);
				window.Sentry?.captureException(error);
			});
	}
}
